/**
 * Picker de canales de Mattermost. Lista canales donde el bot es miembro.
 * Filtra DMs/group DMs (solo type O y P).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Check, AlertCircle, Lock, Hash } from "lucide-react";
import clsx from "clsx";
import { api, ApiError } from "@/lib/api";

interface MattermostChannel {
  id: string;
  teamId: string;
  name: string;
  displayName: string;
  type: "O" | "P";
  purpose?: string;
}

export function MattermostChannelPicker({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (id: string | undefined) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const { data, error } = useQuery<{ channels: MattermostChannel[] }>({
    queryKey: ["integrations", "mm", "channels"],
    queryFn: () => api.get("/integrations/mattermost/channels"),
    staleTime: 5 * 60_000,
  });

  const channels = useMemo(() => data?.channels ?? [], [data]);
  const selected = channels.find((c) => c.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return channels.slice(0, 12);
    return channels
      .filter((c) => c.displayName.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))
      .slice(0, 12);
  }, [channels, query]);

  if (error instanceof ApiError && error.status === 503) {
    return (
      <div className="rounded-md bg-base-200/60 px-3 py-2 text-xs text-base-content/65 flex items-start gap-2">
        <AlertCircle className="size-3.5 mt-0.5 shrink-0 text-warning" />
        <span>
          Mattermost bot no configurado. Define{" "}
          <code className="font-mono">MATTERMOST_BOT_TOKEN</code> en wrangler para usar el picker.
          Alternativa: deja vacío y usa solo el webhook URL abajo.
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      {selected ? (
        <div className="card-elevated p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <ChannelIcon type={selected.type} />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{selected.displayName}</div>
              <div className="text-xs text-base-content/55 truncate font-mono">{selected.name}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(undefined);
              setQuery("");
            }}
            className="btn btn-ghost btn-xs"
          >
            Cambiar
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder="Buscar canal de Mattermost"
              className="input input-bordered w-full pl-9"
            />
          </div>
          {open && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 card-elevated max-h-64 overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-base-content/55 italic">
                  {query
                    ? "Sin coincidencias."
                    : channels.length === 0
                      ? "El bot no es miembro de ningún canal aún. Invítalo desde Mattermost."
                      : "Empieza a escribir para buscar."}
                </div>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(c.id);
                      setQuery("");
                      setOpen(false);
                    }}
                    className={clsx(
                      "w-full px-3 py-2 text-left rounded-md hover:bg-base-200 transition-colors flex items-center gap-2",
                      c.id === value && "bg-base-200",
                    )}
                  >
                    <ChannelIcon type={c.type} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{c.displayName}</div>
                      <div className="text-xs text-base-content/55 truncate font-mono">
                        {c.name}
                      </div>
                    </div>
                    {c.id === value && <Check className="size-3.5 text-primary shrink-0" />}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChannelIcon({ type }: { type: "O" | "P" }) {
  if (type === "P") return <Lock className="size-3.5 text-base-content/55 shrink-0" />;
  return <Hash className="size-3.5 text-base-content/55 shrink-0" />;
}
